import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import styles from './css/index.module.scss';
import PageTitle from '../../components/PageTitle';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import IconButton from '../../components/IconButton';
import CancelEditingIcon from '../../assets/formIcons/cancelEditing.svg';
import EmptyHeight from '../../components/EmptyHeight';

function ViewSingleOperation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const operationId = searchParams.get('id');

  const [gettingLoading, setGettingLoading] = useState(true);
  const [gettingError, setGettingError] = useState('');
  const [singleOperation, setSingleOperation] = useState(null);

  const onCancelClick = () => {
    navigate('/operations/view');
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'parsingEventsFromKontramarka':
        return 'Парсинг Kontramarka';
      case 'parsingEventsFromFienta':
        return 'Парсинг Fienta';
      case 'parsingEventsFromEventim':
        return 'Парсинг Eventim';
      case 'sendNewOrganizationsNotifications':
        return 'Отправка уведомлений о новых организациях';
      case 'sendNewEventsNotifications':
        return 'Отправка уведомлений о новых мероприятиях';
      case 'sendNewOnlineOrganizationsNotifications':
        return 'Отправка уведомлений о новых онлайн организациях';
      default:
        return type;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Выполняется';
      case 'success':
        return 'Успешно';
      case 'error':
        return 'Ошибка';
      default:
        return status;
    }
  };

  const parseStatistics = (statistics) => {
    if (!statistics) return null;
    try {
      return JSON.parse(statistics);
    } catch (e) {
      return null;
    }
  };

  const parseInfoText = (infoText) => {
    if (!infoText) return null;
    try {
      return JSON.parse(infoText);
    } catch (e) {
      return null;
    }
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getOperation,
      payload: { id: operationId },
      warnErrorText: 'while getting single operation',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    setSingleOperation(data.operation);
  };

  useEffect(() => {
    initData().then();
  }, []);

  const statisticsParsed = singleOperation ? parseStatistics(singleOperation.statistics) : null;
  const infoTextParsed = singleOperation ? parseInfoText(singleOperation.info_text) : null;

  return (
    <div className={styles.wrapper}>
      <PageTitle
        text="Просмотр операции"
        RightComponent={(
          <div className={styles.buttonsRow}>
            <IconButton
              svgPath={CancelEditingIcon}
              tooltipText="Отменить"
              onClick={onCancelClick}
            />
          </div>
        )}
      />
      {gettingLoading
        ? <p className={styles.infoText}>Загрузка</p>
        : gettingError
          ? <p className={styles.infoText}>{gettingError}</p>
          : singleOperation ? (
            <div className={styles.container}>
              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Тип</p>
                  </div>
                  <p className={styles.text}>
                    {getTypeText(singleOperation.type)}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Админ</p>
                  </div>
                  <p className={styles.text}>
                    {singleOperation.admin?.display_name || singleOperation.admin?.email || '—'}
                  </p>
                  {singleOperation.admin?.email && (
                    <>
                      <EmptyHeight height={16} />
                      <p className={styles.textGray}>
                        {singleOperation.admin.email}
                      </p>
                    </>
                  )}
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Время запуска</p>
                    <p className={styles.infoTitle}>
                      {singleOperation.createdAt ? moment(singleOperation.createdAt).format('DD.MM.YYYY') : '—'}
                    </p>
                  </div>
                  <p className={styles.text}>
                    {singleOperation.createdAt ? moment(singleOperation.createdAt).format('DD.MM.YYYY HH:mm:ss') : '—'}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Время завершения</p>
                    <p className={styles.infoTitle}>
                      {singleOperation.finish_time ? moment(singleOperation.finish_time).format('DD.MM.YYYY') : '—'}
                    </p>
                  </div>
                  <p className={styles.text}>
                    {singleOperation.finish_time ? moment(singleOperation.finish_time).format('DD.MM.YYYY HH:mm:ss') : '—'}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Статус</p>
                  </div>
                  <p className={styles.text}>
                    {getStatusText(singleOperation.status)}
                  </p>
                </div>
              </div>

              <div className={styles.column}>

                {statisticsParsed && (
                  <div className={`${styles.infoBlock} ${styles.infoBlockWide}`}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Статистика</p>
                    </div>
                    <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(statisticsParsed, null, 2)}
                    </pre>
                  </div>
                )}
                {!statisticsParsed && singleOperation.statistics && (
                  <div className={`${styles.infoBlock} ${styles.infoBlockWide}`}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Статистика</p>
                    </div>
                    <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {singleOperation.statistics}
                    </pre>
                  </div>
                )}
                {infoTextParsed && (
                  <div className={`${styles.infoBlock} ${styles.infoBlockWide}`}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Информация</p>
                    </div>
                    <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(infoTextParsed, null, 2)}
                    </pre>
                  </div>
                )}
                {!infoTextParsed && singleOperation.info_text && (
                  <div className={`${styles.infoBlock} ${styles.infoBlockWide}`}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Информация</p>
                    </div>
                    <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {singleOperation.info_text}
                    </pre>
                  </div>
                )}
              </div>

              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Текст ошибки</p>
                  </div>
                  <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: singleOperation.error_text ? '#E53E3E' : 'unset' }}>
                    {singleOperation.error_text || '-'}
                  </pre>
                </div>
              </div>
            </div>
          ) : null}
    </div>
  );
}

export default ViewSingleOperation;
